import { GenericAbortSignal } from 'axios';

import JobProcess, { JobProcessOptions } from 'models/flexible/JobProcess';
import { Notification, NotificationOptions } from 'models/flexible/Notifications';
import { Paginated } from 'models/flexible/Paginated';
import type { User as UserType } from 'models/flexible/User';
import User, { UserOptions } from 'models/flexible/User';
import type {
  AdminUserCreateParams,
  AdminUserIndexFields,
  AdminUserIndexInclude,
  AdminUserShowFields,
  AdminUserShowInclude,
  AdminUserUpdateParams,
} from 'services/api/fields';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const adminUsersQueryKeys = {
  all: ['adminUsers'] as const,
  index: (params?: Record<string, any>) => ['adminUsers', params] as const,
  show: (id: string) => ['adminUsers', id] as const,
  notificationPreferences: (id: string) => ['adminUsers', id, 'notificationPreferences'] as const,
};

const URL = 'admin/users';

export type AdminUserIndexParams = BaseIndexQueryParams & {
  clientId?: string;
  clientType?: string;
  enabledProviderType?: string | string[];
  groupId?: string;
  roleId?: string;
  active?: string;
  hasExtension?: string;
};

export async function adminIndexUsers<I extends AdminUserIndexInclude = never>(
  params: AdminUserIndexParams,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Paginated<UserType<AdminUserIndexFields<I>>>> {
  const include = includes?.length ? includes.join(',') : undefined;
  return http.get<Paginated<UserOptions>>(URL, { params: { ...params, include }, signal }).then((res) => ({
    ...res.data,
    data: res.data.data.map((x) => new User(x)),
  })) as unknown as Promise<Paginated<UserType<AdminUserIndexFields<I>>>>;
}

export async function adminShowUser<I extends AdminUserShowInclude = never>(
  id: string,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<UserType<AdminUserShowFields<I>>> {
  const include = includes?.length ? includes.join(',') : undefined;
  return http
    .get<UserOptions>(`${URL}/${id}`, { params: { include }, signal })
    .then((res) => new User(res.data)) as unknown as Promise<UserType<AdminUserShowFields<I>>>;
}

export async function adminCreateUser(params: AdminUserCreateParams) {
  return http.post<UserOptions>(URL, params).then((res) => new User(res.data));
}

export async function adminUpdateUser(id: string, params: AdminUserUpdateParams, include?: string) {
  return http
    .patch<UserOptions>(`${URL}/${id}`, params, { params: include ? { include } : undefined })
    .then((res) => new User(res.data));
}

export async function adminDeleteUser(id: string) {
  return http.delete(`${URL}/${id}`);
}

export async function adminReactivateUser(id: string) {
  return http
    .post<UserOptions>(`${URL}/${id}/reactivate`, null, {
      params: { include: 'client,allowedGroupNames' },
    })
    .then((res) => new User(res.data));
}

export async function adminResendInvitation(id: string) {
  return http.post(`${URL}/${id}/resend_invite`);
}

export async function adminCreateUserExport(params: Record<string, any>) {
  return http.post<JobProcessOptions>(`${URL}/exports`, params).then((res) => new JobProcess(res.data));
}

export async function adminFetchUserNotificationPreferences(userId: string, signal?: GenericAbortSignal) {
  return http
    .get<{ data: NotificationOptions[] }>(`${URL}/${userId}/notification_preferences`, {
      params: { include: 'groupType,scopes' },
      signal,
    })
    .then((res) => res.data.data.map((n) => new Notification(n)));
}
